import { Schema } from './schema';
import { Section } from '@jbc-year-end-adj/2024/components/ui/Section';
import { FormSection } from '@jbc-year-end-adj/2024/components/form/FormSection';
import { Row } from '@jbc-year-end-adj/2024/components/form/Row';
import { Input } from '@jbc-year-end-adj/2024/components/form/Input';
import { DateField } from '@jbc-year-end-adj/2024/components/react-hook-form/DateField';
import { Radio } from '@jbc-year-end-adj/2024/components/form/Radio';
import { PostcodeInput } from '@jbc-year-end-adj/2024/components/form/PostcodeInput';
import { SelectField } from '@jbc-year-end-adj/2024/components/react-hook-form/SelectField';
import React, { FC, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { usePrefectureOptions } from '@jbc-year-end-adj/2024/hooks/usePrefectureOptions';
import { AmountField } from 'components/react-hook-form/AmountField';
import { suppressFetchAddressError, useFetchAddressForEmployee } from '@jbc-year-end-adj/2024/hooks/useAddress';
export const BasicInfoForm: FC = () => {
  const methods = useFormContext<Schema>();
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    trigger
  } = methods;

  const isDisasterSufferer = watch('basicInfos.isDisasterSufferer') === 'disasterSufferer';

  const { prefectureOptions } = usePrefectureOptions();
  const fetchAddress = useFetchAddressForEmployee();
  const [shouldFetchAddress, setShouldFetchAddress] = useState(false);
  const postcode0 = watch('basicInfos.postcode0');
  const postcode1 = watch('basicInfos.postcode1');

  useEffect(() => {
    if (shouldFetchAddress) {
      if (postcode0 && postcode1) {
        let cancelled = false;
        fetchAddress(`${postcode0}${postcode1}`)
          .catch(suppressFetchAddressError)
          .then(address => {
            if (cancelled || !address) return;

            const prefecture = prefectureOptions.find(option => option.label === address.prefecture);
            setValue('basicInfos.prefectureId', prefecture?.value ?? '');
            setValue('basicInfos.city', address.city);
            setValue('basicInfos.street', address.street);

            trigger(['basicInfos.prefectureId', 'basicInfos.city', 'basicInfos.street']);
          });
        return () => {
          cancelled = true;
        };
      }
    } else {
      setShouldFetchAddress(true);
    }
    return;
  }, [postcode0, postcode1]);

  return (
    <Section>
      <Section.Header>基本情報</Section.Header>
      <Section.Body>
        <FormSection>
          <Row>
            <Input
              label="姓"
              {...register('basicInfos.lastName')}
              error={errors.basicInfos?.lastName?.message}
              isError={!!errors.basicInfos?.lastName?.message}
              required
            />
            <Input
              label="名"
              {...register('basicInfos.firstName')}
              error={errors.basicInfos?.firstName?.message}
              isError={!!errors.basicInfos?.firstName?.message}
              required
            />
          </Row>

          <Row>
            <Input
              label="姓(カナ)"
              {...register('basicInfos.lastNameKana')}
              error={errors.basicInfos?.lastNameKana?.message}
              isError={!!errors.basicInfos?.lastNameKana?.message}
              required
            />
            <Input
              label="名(カナ)"
              {...register('basicInfos.firstNameKana')}
              error={errors.basicInfos?.firstNameKana?.message}
              isError={!!errors.basicInfos?.firstNameKana?.message}
              required
            />
          </Row>

          <DateField
            label="生年月日"
            note="例）1981/01/23、S56.1.23"
            name="basicInfos.birthday"
            error={errors.basicInfos?.birthday?.message}
            required
          />

          <Radio.Container label="性別" required>
            <Radio value="male" {...register('basicInfos.gender')}>
              男性
            </Radio>
            <Radio value="female" {...register('basicInfos.gender')}>
              女性
            </Radio>
          </Radio.Container>

          <PostcodeInput
            required
            note="現在の住所をご記入ください。"
            errors={[errors.basicInfos?.postcode0?.message ?? '', errors.basicInfos?.postcode1?.message ?? ''].filter(error => error)}
            firstCodeProps={{
              isError: !!errors.basicInfos?.postcode0?.message,
              ...register('basicInfos.postcode0')
            }}
            lastCodeProps={{
              isError: !!errors.basicInfos?.postcode1?.message,
              ...register('basicInfos.postcode1')
            }}
          />

          <Row>
            <SelectField
              label="都道府県"
              name="basicInfos.prefectureId"
              options={prefectureOptions}
              error={errors.basicInfos?.prefectureId?.message}
              required
            />
            <Input
              label="市区町村"
              {...register('basicInfos.city')}
              error={errors.basicInfos?.city?.message}
              isError={!!errors.basicInfos?.city?.message}
              required
            />
          </Row>

          <Row>
            <Input
              label="丁目番地号"
              {...register('basicInfos.street')}
              error={errors.basicInfos?.street?.message}
              isError={!!errors.basicInfos?.street?.message}
            />
            <Input
              label="建物名"
              {...register('basicInfos.building')}
              error={errors.basicInfos?.building?.message}
              isError={!!errors.basicInfos?.building?.message}
            />
          </Row>

          <Radio.Container label="外国人区分" required>
            <Radio value="japanese" {...register('basicInfos.nationalType')}>
              日本人
            </Radio>
            <Radio value="foreigner" {...register('basicInfos.nationalType')}>
              外国人
            </Radio>
          </Radio.Container>

          <Radio.Container
            label="（源泉徴収票）災害者"
            note="給与所得者が災害減免法により源泉所得税及び復興特別所得税の徴収猶予又は還付を受けた場合は年末調整の対象外になります。"
            required
          >
            <Radio value="notDisasterSufferer" {...register('basicInfos.isDisasterSufferer')}>
              対象外
            </Radio>
            <Radio value="disasterSufferer" {...register('basicInfos.isDisasterSufferer')}>
              災害者
            </Radio>
          </Radio.Container>

          {isDisasterSufferer && (
            <AmountField
              label="徴収猶予税額"
              {...register('basicInfos.taxPostponementAmount')}
              error={errors.basicInfos?.taxPostponementAmount?.message}
              isError={!!errors.basicInfos?.taxPostponementAmount?.message}
              required
            />
          )}
        </FormSection>
      </Section.Body>
    </Section>
  );
};
